import { useTranslate } from "@/components/base/providers/TranslateProv";
import Section from "@/components/shared/common/Section";
import Checkbox from "@/components/shared/Form/Checkbox";
import CustomSelect from "@/components/shared/Form/CustomSelect";
import Input from "@/components/shared/Form/Input";
import Countries from "@/public/Data/Countries";
import { createOptionsArray } from "@/utils/object/objectUtils";
import { Mail, Phone, Workflow } from "lucide-react";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import "react-phone-number-input/style.css";

type Props = {
  control: any;
  errors: any;
  selectedMethod: string;
  setWhatsappNumber: any;
  whatsappNumber: any;
  phone: any;
  setSelectedMethod: any;
  setPhone: (value: any) => void;
  message: any;
  setMessage: (value: any) => void;
};

export default function UserInput({
  control,
  errors,
  selectedMethod,
  setSelectedMethod,
  phone,
  setPhone,
  whatsappNumber,
  setWhatsappNumber,
  message,
  setMessage,
}: Props) {
  const { locale }: { locale: "ar" | "en" } = useTranslate();
  const countries = Countries(locale);

  const countryOptions = createOptionsArray(countries, "id", `name`);

  const translations = {
    en: {
      phoneNumber: "Phone Number",
      whatsappNumber: "WhatsApp Number",
      percentage: "Percentage Obtained",
      graduationYear: "Year of High School Graduation",
      countryOfGraduation: "Country of Graduation",
      bestContactMethod: "Best Method of Contact",
      messagePlaceholder: "Your message",
      whatsapp: "WhatsApp",
      email: "Email",
      phone: "Phone",
      yourMessage: "Your Message",
    },
    ar: {
      phoneNumber: "رقم الهاتف",
      whatsappNumber: "رقم الواتس",
      percentage: "النسبة الحاصل عليها",
      graduationYear: "سنة الحصول على الشهادة الثانوية",
      countryOfGraduation: "بلد الحصول على الشهادة",
      bestContactMethod: "أفضل طريقة للتواصل",
      messagePlaceholder: "الرسالة الخاصة بك",
      whatsapp: "واتساب",
      email: "بريد الإلكتروني",
      phone: "هاتف",
      yourMessage: "الرسالة الخاصة بك",
    },
  };

  const t = translations[locale];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row items-center justify-between gap-5">
        <Section title={t.phoneNumber}>
          <PhoneInputWithCountrySelect
            placeholder={t.phoneNumber}
            defaultCountry="EG"
            value={phone}
            onChange={setPhone}
          />
        </Section>
        <Section title={t.whatsappNumber}>
          <PhoneInputWithCountrySelect
            placeholder={t.whatsappNumber}
            defaultCountry="EG"
            value={whatsappNumber}
            onChange={setWhatsappNumber}
          />
        </Section>
      </div>
      <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-5">
        <Section title={t.percentage}>
          <Input
            name="percentage"
            placeholder={t.percentage}
            control={control}
            errors={errors}
          />
        </Section>
        <Section title={t.graduationYear}>
          <Input
            name="year_obtaining_certificate"
            placeholder={t.graduationYear}
            control={control}
            errors={errors}
          />
        </Section>
        <Section title={t.countryOfGraduation}>
          <CustomSelect
            name="country_obtaining_certificate"
            options={countryOptions}
            control={control}
            errors={errors}
          />
        </Section>
      </div>
      <div className="w-full flex-wrap flex flex-col lg:flex-row items-center justify-between gap-5">
        <Section title={t.bestContactMethod}>
          <div className="flex items-start flex-wrap justify-start gap-4 mt-3">
            <CommunicationMethod
              control={control}
              errors={errors}
              method="0"
              label={t.whatsapp}
              Icon={Workflow}
              selectedMethod={selectedMethod}
              setSelectedMethod={setSelectedMethod}
            />
            <CommunicationMethod
              control={control}
              errors={errors}
              method="1"
              label={t.email}
              Icon={Mail}
              selectedMethod={selectedMethod}
              setSelectedMethod={setSelectedMethod}
            />
            <CommunicationMethod
              control={control}
              errors={errors}
              method="2"
              label={t.phone}
              Icon={Phone}
              selectedMethod={selectedMethod}
              setSelectedMethod={setSelectedMethod}
            />
          </div>
        </Section>
      </div>
      <div className="flex flex-col lg:flex-row items-center justify-between gap-5">
        <Section title={t.yourMessage}>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            name={"message"}
            className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            placeholder={t.messagePlaceholder}
          />
        </Section>
      </div>
    </div>
  );
}

const CommunicationMethod = ({
  method,
  label,
  Icon,
  selectedMethod,
  errors,
  control,
  setSelectedMethod,
}: {
  method: string;
  label: string;
  Icon: any;
  selectedMethod: string;
  control: any;
  errors: any;
  setSelectedMethod: (value: string) => void;
}) => (
  <div
    onClick={() => setSelectedMethod(method)}
    className={`flex items-center flex-col gap-2 p-5 w-[150px] rounded-lg cursor-pointer 
      ${
        selectedMethod === method
          ? "bg-blue-100 border-2 border-blue-500 text-blue-700"
          : "bg-gray-100"
      }`}
  >
    <Checkbox
      control={control}
      errors={errors}
      name="communicate"
      valueIn={selectedMethod}
      checked={selectedMethod === method}
    />
    <Icon />
    <span>{label}</span>
  </div>
);
